import { Receipt, Info } from "lucide-react";
import {
  DocRow, GeneratingOverlay, EmptyState, SavingBanner, formatMoney,
  GenerateButton, FreshnessNote,
} from "./shared";
import s from "./documents.module.css";

const sum = (arr) => arr.reduce((t, d) => t + (d.amount || 0), 0);

/**
 * Invoices panel — sales invoices, credit notes & debit notes (type INVOICE).
 *
 * Generation routes through `generateInvoiceFromBooking`, which builds one sales
 * invoice per product family (flight / hotel / transfer / car rental). Regenerate
 * forces an in-place refresh of every existing family invoice — same immutable
 * number, rebuilt from the latest pricing & VAT, with a fresh PDF.
 */
const InvoicesPanel = ({
  docs, generating, enabled, onGenerate, onDownload, onView, onSend, downloading, newDocIds,
  lastGeneratedAt, isStale,
}) => {
  const hasPendingSave = docs.some((d) => !d.pdfUrl && !d.pdfStuck);

  const sales = docs.filter((d) => d.documentSubtype === "sales_invoice");
  const credits = docs.filter((d) => d.documentSubtype === "credit_invoice");
  const debits = docs.filter((d) => d.documentSubtype === "debit_notes");
  const invoiced = sum(sales) + sum(debits);
  const credited = sum(credits);
  const net = invoiced - credited;

  const generateBtn = (
    <GenerateButton
      count={docs.length}
      generating={generating}
      onClick={onGenerate}
      noun="Invoice"
      disabled={!enabled || hasPendingSave}
    />
  );

  return (
    <div>
      <div className={s.panelHead}>
        <div>
          <div className={s.panelTitle}>
            <Receipt size={16} /> Invoices
            {!enabled && <span className={`${s.chip} ${s.chipNeutral}`}>Coming soon</span>}
          </div>
          <div className={s.panelSubtitle}>
            {docs.length} document{docs.length !== 1 ? "s" : ""} · sales invoices, credit &amp; debit notes
          </div>
        </div>
        <div className={s.panelActions}>{generateBtn}</div>
      </div>

      {docs.length > 0 && (
        <FreshnessNote
          lastGeneratedAt={lastGeneratedAt}
          isStale={isStale}
          onRegenerate={enabled ? onGenerate : undefined}
          regenerating={generating}
          pending={hasPendingSave}
        />
      )}

      {/* VAT / generation guidance */}
      <div className={s.infoBanner}>
        <Info size={15} />
        <span>
          {enabled ? (
            <>
              Amounts are shown <strong>VAT-inclusive</strong>. The VAT regime
              (<strong>Normal VAT</strong> or <strong>TOMS</strong> margin) is derived by the system
              from the booking and locked once an invoice is issued.
            </>
          ) : (
            <>
              Invoice generation from booking data is <strong>coming soon</strong>. This screen and its
              loading states are ready — amounts will be <strong>VAT-inclusive</strong> with the VAT regime
              (Normal VAT / TOMS) derived automatically.
            </>
          )}
        </span>
      </div>

      {/* Summary strip — only when invoices exist */}
      {docs.length > 0 && (
        <div className={s.summaryStrip}>
          <div className={s.summaryCard}>
            <div className={s.summaryLabel}>Invoiced</div>
            <div className={s.summaryValue}>{formatMoney(invoiced)}</div>
          </div>
          <div className={s.summaryCard}>
            <div className={s.summaryLabel}>Credited</div>
            <div className={`${s.summaryValue} ${s.summaryValueCredit}`}>
              {credited > 0 ? "− " : ""}{formatMoney(credited)}
            </div>
          </div>
          <div className={s.summaryCard}>
            <div className={s.summaryLabel}>Net invoiced</div>
            <div className={`${s.summaryValue} ${s.summaryValueNet}`}>{formatMoney(net)}</div>
          </div>
        </div>
      )}

      {generating && docs.length === 0 && (
        <GeneratingOverlay
          title="Generating Invoice"
          subtitle="Building the sales invoice from pricing & VAT…"
          accent="var(--primary-color, #f5a51e)"
        />
      )}

      {!generating && docs.length === 0 && (
        <EmptyState
          Icon={Receipt}
          title="No invoices yet"
          hint="Generate a sales invoice from this booking’s pricing & VAT. Amounts stay VAT-inclusive and the VAT regime is derived automatically."
          action={generateBtn}
        />
      )}

      {docs.length > 0 && (
        <div className={s.list}>
          {docs.map((doc, i) => (
            <DocRow
              key={doc.id}
              doc={doc}
              index={i}
              isNew={newDocIds.has(doc.id)}
              onDownload={onDownload}
              onView={onView}
              onSend={onSend}
              downloading={downloading}
              regenerating={generating}
            />
          ))}
          {hasPendingSave && <SavingBanner />}
        </div>
      )}
    </div>
  );
};

export default InvoicesPanel;
